/**
 * InvoiceTableDesktop Component - Desktop table view with improved hierarchy
 * Balance-first, action-aware design
 */

import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Tooltip,
  Box,
  Typography,
} from "@mui/material";
import {
  Send,
  Visibility,
  Download,
  Schedule,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import FollowUpMention from "../../followups/components/FollowUpMention.jsx";
import { logError } from "@/shared/utils/logger.js";
import { getInvoiceDisplayStatus } from "../utils/getInvoiceDisplayStatus.js";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 8,
  boxShadow: "none",
  border: "1px solid rgba(0, 0, 0, 0.12)",
}));

const StyledTableRow = styled(TableRow)(({ theme, isOverdue, isPaid }) => ({
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  opacity: isPaid ? 0.7 : 1,
  borderLeft: isOverdue
    ? `3px solid ${theme.palette.error.main}`
    : "3px solid transparent",
}));

const BalanceCell = styled(TableCell)(({ theme, isOverdue }) => ({
  fontWeight: 700,
  fontSize: "1rem",
  color: isOverdue ? theme.palette.error.main : "rgba(0, 0, 0, 0.87)",
}));

const PaidCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  color: "rgba(0, 0, 0, 0.6)",
}));

const InvoiceIdCell = styled(TableCell)({
  fontFamily: "monospace",
  fontSize: "0.875rem",
  color: "rgba(0, 0, 0, 0.6)",
});

const ActionsCell = styled(TableCell)({
  whiteSpace: "nowrap",
});

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  try {
    return format(new Date(dateString), "MMM dd, yyyy");
  } catch {
    return "Invalid date";
  }
};

const calculateUrgency = (dueDate, status) => {
  if (!dueDate || status === "paid" || status === "cancelled") {
    return { isOverdue: false, isDueSoon: false, daysUntilDue: null };
  }

  const now = new Date();
  const due = new Date(dueDate);
  const diffTime = due.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return {
    isOverdue: diffDays < 0,
    isDueSoon: diffDays >= 0 && diffDays <= 3,
    daysUntilDue: diffDays,
  };
};

const getStatusAction = (status) => {
  switch (status) {
    case "draft":
      return { label: "Send", icon: Send };
    case "sent":
      return { label: "Remind", icon: Schedule };
    case "partial":
      return { label: "Follow Up", icon: Schedule };
    case "overdue":
      return { label: "Remind", icon: Schedule };
    default:
      return { label: "View", icon: Visibility };
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "draft":
      return "default";
    case "sent":
      return "info";
    case "paid":
      return "success";
    case "partial":
      return "warning";
    case "overdue":
      return "error";
    default:
      return "default";
  }
};

/**
 * InvoiceTableDesktop Component
 * @param {Object} props
 * @param {Array} props.invoices - Array of invoice objects
 * @param {Set} props.downloadingPdfIds - Set of invoice IDs currently downloading PDF
 * @param {Function} props.onViewInvoice - Callback when viewing invoice
 * @param {Function} props.onDownloadPdf - Callback when downloading PDF
 * @param {Function} props.onSendInvoice - Callback when sending invoice
 * @param {Function} props.onRemindInvoice - Callback when reminding about invoice
 * @param {Function} props.onFollowUpNavigate - Callback when navigating to follow-up
 */
const InvoiceTableDesktop = ({
  invoices,
  downloadingPdfIds = new Set(),
  onViewInvoice,
  onDownloadPdf,
  onSendInvoice,
  onRemindInvoice,
  onFollowUpNavigate,
}) => {
  const handlePrimaryAction = (invoice) => {
    try {
      const status = getInvoiceDisplayStatus(invoice);
      if (status === "draft" && onSendInvoice) {
        onSendInvoice(invoice);
      } else if (
        (status === "sent" || status === "overdue") &&
        onRemindInvoice
      ) {
        onRemindInvoice(invoice);
      } else if (status === "partial" && onFollowUpNavigate) {
        onFollowUpNavigate(invoice);
      } else if (onViewInvoice) {
        onViewInvoice(invoice);
      }
    } catch (error) {
      logError("Failed to execute primary action", {
        invoiceId: invoice.id || invoice._id,
        status: getInvoiceDisplayStatus(invoice),
        error: error.message,
      });
    }
  };

  return (
    <StyledTableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Balance</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((invoice) => {
            const invoiceId = invoice.id || invoice._id;
            const balance =
              invoice.paymentSummary?.balance || invoice.total || 0;
            const paid = invoice.paymentSummary?.totalPaid || 0;
            const total = invoice.total || 0;
            const status = getInvoiceDisplayStatus(invoice);
            const urgency = calculateUrgency(invoice.dueDate, status);
            const statusAction = getStatusAction(status);
            const ActionIcon = statusAction.icon;
            const isPaid = status === "paid";

            return (
              <StyledTableRow
                key={invoiceId}
                isOverdue={urgency.isOverdue}
                isPaid={isPaid}
              >
                <InvoiceIdCell>{invoiceId}</InvoiceIdCell>
                <TableCell>
                  {invoice.customerName || invoice.customerPhone || "N/A"}
                </TableCell>
                <BalanceCell isOverdue={urgency.isOverdue}>
                  {formatCurrency(balance)}
                  {paid > 0 && (
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        fontSize: "0.75rem",
                        fontWeight: 400,
                        color: "rgba(0, 0, 0, 0.6)",
                        mt: 0.5,
                      }}
                    >
                      {formatCurrency(paid)} paid
                    </Box>
                  )}
                </BalanceCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={status}
                      color={getStatusColor(status)}
                      size="small"
                      sx={{ textTransform: "capitalize" }}
                    />
                    {urgency.isDueSoon && !urgency.isOverdue && (
                      <Tooltip
                        title={`Due in ${urgency.daysUntilDue} days`}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "warning.main",
                          }}
                        />
                      </Tooltip>
                    )}
                    {invoice?.followUpSummary && (
                      <FollowUpMention
                        summary={invoice.followUpSummary}
                        onClick={
                          onFollowUpNavigate
                            ? () => onFollowUpNavigate(invoice)
                            : undefined
                        }
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {formatDate(invoice.dueDate)}
                    </Typography>
                    {urgency.isOverdue && (
                      <Typography
                        variant="caption"
                        sx={{ color: "error.main", fontWeight: 600 }}
                      >
                        OVERDUE
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <ActionsCell>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Tooltip title={statusAction.label}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ActionIcon />}
                        onClick={() => handlePrimaryAction(invoice)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                        }}
                      >
                        {statusAction.label}
                      </Button>
                    </Tooltip>
                    {status !== "sent" &&
                      status !== "paid" &&
                      status !== "cancelled" &&
                      status !== "draft" &&
                      onSendInvoice && (
                        <Tooltip title="Send Invoice">
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<Send />}
                            onClick={() => onSendInvoice(invoice)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                            }}
                          >
                            Send
                          </Button>
                        </Tooltip>
                      )}
                    {statusAction.label !== "View" && (
                      <Tooltip title="View Details">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => onViewInvoice(invoice)}
                        >
                          View
                        </Button>
                      </Tooltip>
                    )}
                    <Tooltip title="Download PDF">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={downloadingPdfIds.has(invoiceId) ? <CircularProgress size={16} /> : <Download />}
                        onClick={() => onDownloadPdf(invoiceId)}
                        disabled={downloadingPdfIds.has(invoiceId)}
                      >
                        PDF
                      </Button>
                    </Tooltip>
                  </Box>
                </ActionsCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default InvoiceTableDesktop;

